import { List, useTable } from '@refinedev/antd';
import { Table, Select, Space, Tag, Button, Input } from 'antd';
import { EyeOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface DriverPostedTrip {
  id: number;
  tripMode: 'SHAHARLAR_ARO' | 'VILOYATLAR_ARO';
  status: string;
  totalSeats: number;
  availableSeats: number;
  pricePerSeat: number;
  departureTime: string;
  departureAddress: string;
  destinationAddress: string;
  acceptsRoadside: boolean;
  createdAt: string;
  driver: {
    id: number;
    fullName: string;
    phone: string;
  };
  departureRegion: { nameUz: string } | null;
  destinationRegion: { nameUz: string } | null;
  departureDistrict: { nameUz: string } | null;
  destinationDistrict: { nameUz: string } | null;
  _count: { bookings: number };
}

const tripModeLabels: Record<string, string> = {
  SHAHARLAR_ARO: 'Shaharlar aro',
  VILOYATLAR_ARO: 'Viloyatlar aro',
};

const tripModeColors: Record<string, string> = {
  SHAHARLAR_ARO: 'blue',
  VILOYATLAR_ARO: 'purple',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Faol',
  FULL: "To'liq",
  DEPARTED: "Yo'lda",
  COMPLETED: 'Yakunlangan',
  CANCELLED: 'Bekor qilingan',
};

const statusColors: Record<string, string> = {
  ACTIVE: 'blue',
  FULL: 'purple',
  DEPARTED: 'orange',
  COMPLETED: 'green',
  CANCELLED: 'red',
};

export const DriverPostedTripList = () => {
  const navigate = useNavigate();
  const [tripModeFilter, setTripModeFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [searchFilter, setSearchFilter] = useState<string | undefined>(undefined);

  const { tableProps } = useTable<DriverPostedTrip>({
    resource: 'admin/driver-posted-trips',
    syncWithLocation: true,
    filters: {
      permanent: [
        ...(tripModeFilter ? [{ field: 'tripMode', operator: 'eq' as const, value: tripModeFilter }] : []),
        ...(statusFilter ? [{ field: 'status', operator: 'eq' as const, value: statusFilter }] : []),
        ...(searchFilter ? [{ field: 'search', operator: 'eq' as const, value: searchFilter }] : []),
      ],
    },
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uz-UZ').format(price) + " so'm";
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <List
      title="Haydovchi sayohatlari"
      headerButtons={() => null}
    >
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="Rejim"
          allowClear
          style={{ width: 180 }}
          value={tripModeFilter}
          onChange={(value) => setTripModeFilter(value)}
          options={[
            { value: 'SHAHARLAR_ARO', label: 'Shaharlar aro' },
            { value: 'VILOYATLAR_ARO', label: 'Viloyatlar aro' },
          ]}
        />
        <Select
          placeholder="Holat"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
          options={[
            { value: 'ACTIVE', label: 'Faol' },
            { value: 'FULL', label: "To'liq" },
            { value: 'DEPARTED', label: "Yo'lda" },
            { value: 'COMPLETED', label: 'Yakunlangan' },
            { value: 'CANCELLED', label: 'Bekor qilingan' },
          ]}
        />
        <Input.Search
          placeholder="Haydovchi nomi/telefoni"
          allowClear
          style={{ width: 250 }}
          onSearch={(value) => setSearchFilter(value || undefined)}
        />
      </Space>

      <Table {...tableProps} rowKey="id" size="small">
        <Table.Column dataIndex="id" title="ID" width={60} />
        <Table.Column
          dataIndex="tripMode"
          title="Rejim"
          width={130}
          render={(mode: string) => (
            <Tag color={tripModeColors[mode] || 'default'}>
              {tripModeLabels[mode] || mode}
            </Tag>
          )}
        />
        <Table.Column
          title="Haydovchi"
          width={180}
          render={(_, record: DriverPostedTrip) => (
            <div>
              <div style={{ fontWeight: 500 }}>{record.driver?.fullName || '-'}</div>
              <div style={{ fontSize: 12, color: '#999' }}>{record.driver?.phone || '-'}</div>
            </div>
          )}
        />
        <Table.Column
          title="Marshrut"
          render={(_, record: DriverPostedTrip) => (
            <Space size={4}>
              <EnvironmentOutlined style={{ color: '#52c41a' }} />
              <span>{record.departureRegion?.nameUz || '-'}</span>
              <span style={{ color: '#999' }}>→</span>
              <EnvironmentOutlined style={{ color: '#f5222d' }} />
              <span>{record.destinationRegion?.nameUz || '-'}</span>
            </Space>
          )}
        />
        <Table.Column
          title="Joylar"
          width={90}
          render={(_, record: DriverPostedTrip) => (
            <Tag color={record.availableSeats > 0 ? 'green' : 'red'}>
              {record.availableSeats}/{record.totalSeats}
            </Tag>
          )}
        />
        <Table.Column
          dataIndex="pricePerSeat"
          title="Narx"
          width={120}
          render={(price: number) => formatPrice(price)}
        />
        <Table.Column
          dataIndex="departureTime"
          title="Jo'nash vaqti"
          width={150}
          render={(date: string) => formatDate(date)}
        />
        <Table.Column
          dataIndex="status"
          title="Holat"
          width={130}
          render={(status: string) => (
            <Tag color={statusColors[status] || 'default'}>
              {statusLabels[status] || status}
            </Tag>
          )}
        />
        <Table.Column
          title="Buyurtmalar"
          width={100}
          render={(_, record: DriverPostedTrip) => (
            <Tag>{record._count?.bookings || 0}</Tag>
          )}
        />
        <Table.Column
          title="Amallar"
          width={80}
          render={(_, record: DriverPostedTrip) => (
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/driver-posted-trips/${record.id}`)}
            />
          )}
        />
      </Table>
    </List>
  );
};
